import React, { useRef, useEffect, useState, useLayoutEffect } from 'react';
import ReactLoader from 'react-loading';

import { withTheme, ThemeProps } from 'styled-components';

import ApplicationOverlayContent from 'components/ApplicationLayout/ApplicationOverlayContent';
import useStoreTranslation from 'hooks/useStoreTranslation';
import {
  TravelHotelContentLocationRequest,
  TravelHotelSearch_HotelLocation_Response,
  TravelHotelSearch_Location_Response,
} from 'models/hotelLocation';
import { HotelLocationsState } from 'reducers/hotelLocations';

import ResultItem from './components/ResultItem';
import { Header, Result, RecentSearches } from './styles';

interface Props extends HotelLocationsState, ThemeProps<any> {
  show: boolean;
  placeHolder: string;
  minLengthForSearch: number;
  onClose: () => void;
  onSearchChange: (value: string) => void;
  onChange: (data?: TravelHotelContentLocationRequest) => void;
}

const HotelLocationsSearch = ({
  show,
  placeHolder,
  bySearch,
  isLoading,
  theme,
  minLengthForSearch,
  onClose,
  onChange,
  onSearchChange,
}: Props) => {
  const { t } = useStoreTranslation();
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLocalLoading, setIsLocalLoading] = useState(false);

  useLayoutEffect(() => {
    searchInputRef.current?.focus();
    setSearchQuery('');
  }, [show]);

  useEffect(() => {
    setIsLocalLoading(isLoading);
  }, [isLoading]);

  const handleSearchInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;

    if (value && value.length > 2) {
      setIsLocalLoading(true);
    }

    setSearchQuery(value);
    onSearchChange(value);
  };

  const handleClickResultItem = (
    type: string,
    hotel?: TravelHotelSearch_HotelLocation_Response,
    location?: TravelHotelSearch_Location_Response,
  ) => {
    if (type === 'location') {
      onChange({
        id: location?.Id,
        description: location?.NameFull,
        isHotel: false,
        name: location?.Name,
      } as TravelHotelContentLocationRequest);
    }

    if (type === 'hotel') {
      onChange({
        id: hotel?.Id,
        description: hotel?.address,
        isHotel: true,
        name: hotel?.hotel_name,
      } as TravelHotelContentLocationRequest);
    }

    onClose();
  };

  const renderLoading = () => (
    <Result.Loading>
      <ReactLoader type="spin" color={theme.loading} width={60} height={60} />
    </Result.Loading>
  );

  const renderResults = (searchQuery: string) => {
    if (
      !isLocalLoading &&
      (bySearch === undefined || (bySearch.hotels.length === 0 && bySearch.locations.length === 0)) &&
      searchQuery.length > minLengthForSearch
    ) {
      return (
        <Result.NotFound>
          <h2>Ops!</h2>
          <span>{t('error.notFound')}</span>
        </Result.NotFound>
      );
    }

    return (
      <>
        {bySearch &&
          bySearch.locations &&
          bySearch?.locations.map((location, index) => (
            <Result.Item key={index} onClick={() => handleClickResultItem('location', undefined, location)}>
              <span>
                <ResultItem
                  key={`location-${location.Type}-${location.Id}-${index}`}
                  location={location}
                  onClick={() => handleClickResultItem('location', undefined, location)}
                  type="location"
                />
              </span>
            </Result.Item>
          ))}

        {bySearch &&
          bySearch.hotels &&
          bySearch?.hotels.map((hotelLocation, index) => (
            <Result.Item
              key={`${index}-hotel-location-${hotelLocation.LocationType}-${hotelLocation.Id}-${index}`}
              onClick={() => handleClickResultItem('hotel', hotelLocation, undefined)}
            >
              <span>
                <ResultItem
                  key={`hotel-location-${hotelLocation.LocationType}-${hotelLocation.Id}-${index}`}
                  hotelLocation={hotelLocation}
                  onClick={() => handleClickResultItem('hotel', hotelLocation, undefined)}
                  type="hotel"
                />
              </span>
            </Result.Item>
          ))}
      </>
    );
  };

  return (
    <ApplicationOverlayContent sidebar show={show} onClose={onClose}>
      <Header>
        <Header.Search
          ref={searchInputRef}
          value={searchQuery}
          placeholder={placeHolder}
          onChange={handleSearchInputChange}
          data-testid="city-search-input"
        />
        <Header.CloseIcon onClick={onClose} />
      </Header>
      <Result data-testid="city-search-result-list">
        {searchQuery.length <= minLengthForSearch && (
          <RecentSearches>{t('citiesSearch.recentSearches')}</RecentSearches>
        )}
        {isLocalLoading ? renderLoading() : renderResults(searchQuery)}
      </Result>
    </ApplicationOverlayContent>
  );
};

export default withTheme(HotelLocationsSearch);
